import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { Box, IconSize, Separator, Text, ToastType, useModalState } from '@bechtel/gds';
import {
  AuthPermission,
  Block,
  BlockCommodityType,
  BlockPoint,
  BreakerPanel,
  BreakerPanelType,
  Equipment,
  EquipmentCode,
  MetadataTableName,
  Question,
  SaveBlockInput,
  SaveBlockResult,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { cloneDeep, filter, find, findIndex, get, includes, isEqual, isNil, map, orderBy, some } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppSpinner,
  ClientGrid,
  CommodityFooter,
  CustomAction,
  ErrorRetry,
  MultiLingualText,
  QuestionModalResponse,
  useAppUser,
  useLanguage,
} from '~/components';
import {
  GET_BLOCKS_BY_COMMODITY,
  GET_EQUIPMENT_CODE_VIEW_STATS,
  GET_EQUIPMENT_VIEW_STATS,
  SAVE_EQUIPMENT_BLOCKS,
} from '~/gql';
import { UseEntityAffectedSubscriptionResult, useTableMetadataQuery, useToast } from '~/hooks';
import MessageModal from '../message-modal/message';
import { BreakerModal } from './breaker-modal';
import { CopyBlocksModal, CopyPointsModal } from './copy-modals';
import { PointSequenceModal } from './point-sequence-modal';
import { Points } from './points';
import { BodyStyled, ButtonStyled, SeparatorStyled } from './styled';

type Commodity = Equipment | EquipmentCode;

interface BlockPointsProps {
  commodity: Commodity;
  blockCommodityType: BlockCommodityType;
}

const MAX_POINTS_ALLOWED = 1024;

export const BlockPoints: React.FC<BlockPointsProps> = ({ commodity, blockCommodityType }) => {
  const { showToast } = useToast();
  const { isAlternateLanguageUsed, getMultiLingualTextByKey } = useLanguage();
  const [blocks, setBlocks] = useState<Block[] | []>([]);
  const [initialBlocks, setInitialBlocks] = useState<Block[] | []>([]);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const { visible, close, open } = useModalState(false);
  const { visible: visibleBreakerModal, close: closeBreakerModal, open: openBreakerModal } = useModalState(false);
  const [uiMessages, setUiMessages] = useState<UiMessageKey[]>([]);
  const [getBlockMetadata, { data: blockMetadata }] = useTableMetadataQuery();
  const [getPointMetadata, { data: pointMetadata }] = useTableMetadataQuery();
  const { userAccount, hasPermission } = useAppUser();
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const panelType =
    blockCommodityType === BlockCommodityType.Equipment
      ? ((commodity as Equipment).equipmentCode?.breakerPanelType as BreakerPanelType)
      : null;
  const entityText = getMultiLingualTextByKey(UiTextEnum.BLOCKS_AND_POINTS);
  const { visible: copyBlocksVisible, open: copyBlocksOpen, close: copyBlocksClose } = useModalState(false);
  const { visible: copyPointsVisible, open: copyPointsOpen, close: copyPointsClose } = useModalState(false);

  //TODO - DELETE after approach finalized for subscriptions
  // const entityInfo = useEntityAffectedSubscription();

  // useEffect(() => {
  //   console.log(entityInfo);
  //   if (
  //     !entityInfo ||
  //     !entityInfo.name ||
  //     !entityInfo.items ||
  //     entityInfo.name !==
  //       (blockCommodityType === BlockCommodityType.Equipment ? 'EQUIPMENT_BLOCK' : 'EQUIPMENT_CODE_BLOCK')
  //   )
  //     return;

  //   const items = entityInfo.items;

  //   setInitialBlocks(items);
  //   setBlocks(items);
  // }, [blockCommodityType, entityInfo]);

  const {
    loading: blocksLoading,
    error: blocksError,
    refetch: blocksRefetch,
    networkStatus: blocksNetworkStatus,
  } = useQuery<{
    blocksByCommodity: Block[];
  }>(GET_BLOCKS_BY_COMMODITY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      commodityId: commodity.id,
      blockCommodityType: blockCommodityType,
    },
    onCompleted: (data) => {
      if (data.blocksByCommodity?.length) {
        const orderedBlocks = orderBy(data.blocksByCommodity, (item) => item.sequenceNumber);
        setInitialBlocks(orderedBlocks);
        setBlocks(orderedBlocks);
        setSelectedBlock(orderedBlocks?.[0]);
      }
    },
  });

  const [saveBlocks, { loading: saveBlocksLoading }] = useMutation<
    { saveBlocks: SaveBlockResult },
    { saveBlockInput: SaveBlockInput }
  >(SAVE_EQUIPMENT_BLOCKS, {
    onCompleted: (data) => {
      const saveBlocksError = get(data, 'saveBlocks.errors');
      const saveBlocksInfo = get(data, 'saveBlocks.infos');
      const updatedBlocks = get(data, 'saveBlocks.blocks');

      if (saveBlocksError) {
        setUiMessages(saveBlocksError);
      } else if (updatedBlocks) {
        if (saveBlocksInfo) setUiMessages(saveBlocksInfo);
        //Set the blocks for UI to reflect any changes that happened in backend as part of calling the mutation.
        //There is a quite a lot of business logic that happens at the backend which may cause the blocks/points to change from what was passed in the input
        setBlocks(updatedBlocks);
        setInitialBlocks(updatedBlocks);
        //Need to set the selectedBlock for BlockPoints grid to update based on the data received from the mutation response.
        //Point grid data is set based on the selectedBlock so this logic is necessary
        const currentSelectedBlock = find(
          updatedBlocks,
          (updatedBlock: Block) => updatedBlock.tag === selectedBlock?.tag
        );
        if (currentSelectedBlock) {
          setSelectedBlock(currentSelectedBlock);
        } else {
          //Need to select the first block, this is required in scenario while deleting the selected block
          setSelectedBlock(updatedBlocks[0]);
        }
        showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [entityText, ''], ToastType.INFO);
      }
    },
    onError: () => {
      showToast(UiTextEnum.ERROR_WHILE_SAVING_TOAST, [entityText, ''], ToastType.WARNING);
    },
    update: (cache, { data }) => {
      cache.writeQuery({
        query: GET_BLOCKS_BY_COMMODITY,
        data: { blocksByCommodity: data?.saveBlocks.blocks },
        variables: {
          commodityId: commodity.id,
          blockCommodityType: blockCommodityType,
        },
      });
    },
    refetchQueries: [
      {
        query:
          blockCommodityType === BlockCommodityType.Equipment
            ? GET_EQUIPMENT_VIEW_STATS
            : GET_EQUIPMENT_CODE_VIEW_STATS,
        variables: {
          id: commodity?.id,
        },
      },
    ],
  });

  useEffect(() => {
    if (!userAccount?.defaultProject?.id) return;
    getBlockMetadata({
      variables: {
        name: MetadataTableName.EQUIPMENT_BLOCK,
        projectId: userAccount?.defaultProject?.id,
      },
    });
    getPointMetadata({
      variables: {
        name: MetadataTableName.EQUIPMENT_BLOCK_POINT,
        projectId: userAccount?.defaultProject?.id,
      },
    });
  }, [userAccount?.defaultProject?.id, getBlockMetadata, getPointMetadata]);

  const onEdited = useCallback((prevBlocks: Block[] | [], block: Block) => {
    const index = prevBlocks?.findIndex((item) => item?.id === block?.id);
    //There can be only one breaker block per equipment/equipment code. So if the current edited block is set as Breaker, set the isBreaker for all other blocks to false
    const existingBlocks = block.isBreaker
      ? map(prevBlocks, (prevBlock) => {
          return { ...prevBlock, isBreaker: false };
        })
      : prevBlocks;
    if (index === -1) {
      if (!block.vendorSide) block.vendorSide = 'L';
      if (!block.isBreaker) block.isBreaker = false; //handle undefined case - by default isBreaker is false
      // Id is required as tag are editable, after saving the grid data needs to be refreshed with the correct ID generated from the backend
      const newBlock = {
        ...block,
        id: block?.id ? block?.id : crypto.randomUUID(),
      };
      setSelectedBlock(newBlock);
      return [...existingBlocks, newBlock];
    } else {
      const updatedBlocks = [...existingBlocks];
      updatedBlocks[index] = block;
      setSelectedBlock(block);
      return updatedBlocks;
    }
  }, []);

  const onBlockEdited = useCallback(
    (block: Block) => {
      setBlocks((prevBlocks) => onEdited(prevBlocks, block));
    },
    [onEdited]
  );

  const onBlockDeleted = useCallback(
    (deletedIDs: string[]) => {
      const updatedBlocks = filter(blocks, (block) => !includes(deletedIDs, block.id));
      if (updatedBlocks?.length) {
        setSelectedBlock(updatedBlocks[0]);
      } else {
        setSelectedBlock(null);
      }
      setBlocks([...updatedBlocks]);
    },
    [blocks]
  );

  const onBlockPointsChanged = useCallback(
    (blockPoints: BlockPoint[], block: Block) => {
      const updatedBlock = { ...block, blockPoints: [...blockPoints] };
      onBlockEdited(updatedBlock);
    },
    [onBlockEdited]
  );

  const onRowDragged = useCallback(
    (fromIndex, toIndex) => {
      const newBlocks = blocks.slice();
      const movedBlock = newBlocks[fromIndex];
      newBlocks.splice(fromIndex, 1);
      newBlocks.splice(toIndex, 0, movedBlock);
      setBlocks(newBlocks);
      return newBlocks;
    },
    [blocks]
  );

  const onSaveBlocks = useCallback(() => {
    saveBlocks({
      variables: {
        saveBlockInput: {
          blocks: map(blocks, (block, index) => {
            return {
              id: block.id,
              tag: block.tag,
              vendorSide: block.vendorSide,
              sequenceNumber: index + 1,
              isBreaker: block.isBreaker,
              blockPoints: map(block.blockPoints, (point) => {
                return {
                  id: point?.id,
                  tag: point?.tag,
                  sequenceNumber: point.sequenceNumber,
                  gangSequence: point.gangSequence,
                  description: point.description,
                  breakerSizeAmps: point.breakerSizeAmps,
                  connectedLoad: point.connectedLoad,
                  revision: point.revision,
                };
              }),
            };
          }),
          commodityId: commodity.id,
          blockCommodityType: blockCommodityType,
        },
      },
    });
  }, [blockCommodityType, blocks, commodity.id, saveBlocks]);

  const initialBlockPoints = useMemo(() => {
    const originalBlock = find(initialBlocks, (block) => block.id === selectedBlock?.id);
    return originalBlock?.blockPoints;
  }, [initialBlocks, selectedBlock?.id]);

  const validationFns = useMemo(() => {
    return {
      tag: (value) => {
        if (isNil(value)) {
          return false;
        }
        return true;
      },
      vendorSide: (value) => {
        if (!includes(['L', 'R'], value)) {
          return false;
        }
        return true;
      },
    };
  }, []);

  const hasTerminations = useMemo(() => {
    return some(
      selectedBlock?.blockPoints,
      (blockPoint: BlockPoint) =>
        blockPoint.equipmentBlockPointView?.customerCount || blockPoint.equipmentBlockPointView?.vendorCount
    );
  }, [selectedBlock?.blockPoints]);

  const openSequenceModal = useCallback(() => {
    if (hasTerminations) {
      setUiMessages([UiMessageKey.CHANGES_NOT_ALLOWED_TERMINATIONS_HAVE_BEEN_MADE]);
      return;
    }
    open();
  }, [hasTerminations, open]);

  const onGridValidate = useCallback((isValid: boolean) => {
    if (isValid) {
      setHasErrors(true);
    } else {
      setHasErrors(false);
    }
  }, []);

  const customColDefs = useMemo(() => {
    return {
      isBreaker: {
        hide: !get(
          commodity,
          blockCommodityType === BlockCommodityType.Equipment ? 'equipmentCode.isBreakerPanel' : 'isBreakerPanel'
        ),
      },
    };
  }, [blockCommodityType, commodity]);

  const onPointsGenerated = useCallback(
    (startSequence, endSequence, leadingZeros) => {
      const blockPoints: BlockPoint[] = [];

      let startValue = startSequence;
      let endValue = endSequence;
      if (startValue > endValue) {
        [startValue, endValue] = [endValue, startValue]; //swap without using temp variable
      }

      startValue = Math.max(1, startValue); //MINIMUM ONE POINT
      endValue = Math.min(MAX_POINTS_ALLOWED, endValue);
      const maxLength = Math.max(String(startValue).length, String(endValue).length);

      const numbersWithLeadingZeros: string[] = [];

      const isReverseNumbering = startSequence > endSequence;
      let sequence = isReverseNumbering ? startSequence - endSequence + 2 : 0;
      for (let i = startValue; i <= endValue; i++) {
        if (startSequence > endSequence) {
          sequence--;
        } else {
          sequence++;
        }
        if (leadingZeros) {
          const paddedNumber = String(i).padStart(maxLength, '0');
          numbersWithLeadingZeros.push(paddedNumber);
          blockPoints.push({
            id: crypto.randomUUID(),
            tag: paddedNumber,
            sequenceNumber: sequence,
          } as BlockPoint);
        } else {
          blockPoints.push({
            id: crypto.randomUUID(),
            tag: i.toString(),
            sequenceNumber: sequence,
          } as BlockPoint);
        }
      }

      if (selectedBlock) onBlockPointsChanged(blockPoints, selectedBlock);
      close();
    },
    [close, onBlockPointsChanged, selectedBlock]
  );

  const updateSubscriptionEntity = useCallback((data: UseEntityAffectedSubscriptionResult) => {
    setInitialBlocks(data.items);
    setBlocks(data.items);
  }, []);

  const onBreakersUpdated = (breakerPanels: BreakerPanel[]) => {
    const existingBlocks = cloneDeep(blocks);

    const currentBlockIndex = findIndex(existingBlocks, (block: Block) => block.id === breakerPanels[0]?.blockId);

    if (currentBlockIndex === -1) return;

    const updatedBlockPoints = map(existingBlocks[currentBlockIndex]?.blockPoints, (blockPoint: BlockPoint) => {
      const breakerPanel = find(breakerPanels, { pointId: blockPoint.id });

      return breakerPanel
        ? {
            ...blockPoint,
            description: breakerPanel.description,
            gangSequence: breakerPanel.gangSequence,
            breakerSizeAmps: breakerPanel.breakerSizeAmps,
            connectedLoad: breakerPanel.connectedLoad,
            revision: breakerPanel.breakerRev,
          }
        : blockPoint;
    });

    if (existingBlocks[currentBlockIndex]) {
      existingBlocks[currentBlockIndex].blockPoints = updatedBlockPoints;
    }
    setBlocks(existingBlocks);
  };

  const breakerBlockItem = blocks?.find((block: Block) => block.isBreaker);

  const onGridInitialize = useCallback((api) => {
    const firstDisplayedColumn = api.getAllDisplayedColumns()[0]?.getColId();
    api.setFocusedCell(0, firstDisplayedColumn);
  }, []);

  if (blocksLoading || blocksNetworkStatus === NetworkStatus.refetch) {
    return (
      <Box p={{ sm: '500' }} flexGrow={{ sm: 1 }} justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }}>
        <AppSpinner size={IconSize.MEDIUM} />
      </Box>
    );
  }

  if (blocksError) {
    return <ErrorRetry refetch={blocksRefetch} />;
  }

  const canEdit = hasPermission(AuthPermission.SET_BLOCK_POINT) && !commodity.isRestricted;

  const onCopyBlocks = (blocks: Block[]) => {
    // Update the state with the new array
    setBlocks([...blocks]);
    // Update the selectedBlock state as well
    setSelectedBlock(blocks[0]);
  };

  const onCopyPoints = (points: BlockPoint[]) => {
    if (selectedBlock) {
      const selectedBlockItem: Block = { ...selectedBlock };
      selectedBlockItem.blockPoints = [...points];

      // Find the index of the selectedBlock in the blocks array
      const index = blocks.findIndex((block) => block.id === selectedBlock.id);

      if (index !== -1) {
        // Create a new array with the updated block
        const updatedBlocks = [...blocks.slice(0, index), selectedBlockItem, ...blocks.slice(index + 1)];

        // Update the state with the new array
        setBlocks(updatedBlocks);
      }

      // Update the selectedBlock state as well
      setSelectedBlock(selectedBlockItem);
    }
  };

  const openCopyPointsModal = () => {
    if (selectedBlock && selectedBlock.blockPoints?.length) {
      setUiMessages([UiMessageKey.EXISTING_POINTS_WILL_BE_DELETED]);
    } else {
      copyPointsOpen();
    }
  };

  const openCopyBlocksModal = () => {
    if (blocks.length) {
      setUiMessages([UiMessageKey.EXISTING_BLOCKS_WILL_BE_DELETED]);
    } else {
      copyBlocksOpen();
    }
  };

  const handleQuestions = async (questionResponses: QuestionModalResponse[]) => {
    setUiMessages([]);
    if (questionResponses[0].response === Question.REPLY_YES) {
      if (questionResponses[0].messageKey === UiMessageKey.EXISTING_BLOCKS_WILL_BE_DELETED) {
        copyBlocksOpen();
      } else if (questionResponses[0].messageKey === UiMessageKey.EXISTING_POINTS_WILL_BE_DELETED) {
        copyPointsOpen();
      }
    }
  };

  return (
    <>
      <Box flexGrow={{ sm: '1' }} flexDirection={{ sm: 'column' }}>
        <BodyStyled>
          <Box px={{ sm: '450' }} flexGrow={{ sm: '1' }} flexDirection={{ sm: 'column' }} py={{ sm: '500' }}>
            <Box mb={{ sm: '400' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
              <Text color="grey.900" size="400" weight="600">
                {isAlternateLanguageUsed
                  ? blockMetadata?.tableMetadata?.alternateDescription
                  : blockMetadata?.tableMetadata?.description}
              </Text>
              <CustomAction
                actionHandler={() => openCopyBlocksModal()}
                text="Copy"
                enableAction={canEdit}
                icon="copy"
              />
            </Box>
            <Box mb={{ sm: '450' }}>
              <Separator size="100%" color="grey.800"></Separator>
            </Box>
            <Box flexBasis={{ sm: '90%' }}>
              <ClientGrid
                name={MetadataTableName.EQUIPMENT_BLOCK}
                rowSelection="single"
                hideLayoutHeader={true}
                onClick={(block: Block) => {
                  setSelectedBlock(block);
                }}
                systemLayoutName="equipment.equipmentBlock"
                rowData={blocks}
                originalRowData={initialBlocks}
                onRowEdited={onBlockEdited}
                onRowsDeleted={onBlockDeleted}
                rowDraggableColumn="tag"
                onRowDragged={onRowDragged}
                validationFns={validationFns}
                onValidate={onGridValidate}
                skipPermission={canEdit}
                updateSubscriptionEntity={updateSubscriptionEntity}
                customColDefs={customColDefs}
                onGridInitialize={onGridInitialize}
                onGridRowSelected={(blocks: Block[] | []) => {
                  setSelectedBlock(blocks?.[0]);
                }}
              ></ClientGrid>
            </Box>
          </Box>
          <SeparatorStyled></SeparatorStyled>
          <Box px={{ sm: '450' }} flexGrow={{ sm: '1' }} flexDirection={{ sm: 'column' }} py={{ sm: '500' }}>
            {selectedBlock?.tag && (
              <>
                <Box mb={{ sm: '400' }} alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-between' }}>
                  <Text color="grey.900" size="400" weight="600">
                    {`${
                      isAlternateLanguageUsed
                        ? pointMetadata?.tableMetadata?.alternateDescription
                        : pointMetadata?.tableMetadata?.description
                    } (${selectedBlock?.tag})`}
                  </Text>
                  <CustomAction
                    actionHandler={() => openCopyPointsModal()}
                    text="Copy"
                    enableAction={canEdit}
                    icon="copy"
                  />
                </Box>
                <Box mb={{ sm: '450' }}>
                  <Separator size="100%" color="grey.800"></Separator>
                </Box>
                <Box flexBasis={{ sm: '90%' }}>
                  <Points
                    blockPointList={selectedBlock.blockPoints}
                    onBlockPointsChanged={(blockPoints) => {
                      onBlockPointsChanged(blockPoints, selectedBlock);
                    }}
                    initialBlockPoints={initialBlockPoints}
                    canEdit={canEdit}
                  ></Points>
                </Box>
                <Box justifyContent={{ sm: 'space-between' }} mb={{ sm: '200' }}>
                  <ButtonStyled
                    secondary={true}
                    onClick={() => {
                      openSequenceModal();
                    }}
                  >
                    <MultiLingualText>Auto</MultiLingualText>
                  </ButtonStyled>
                  <ButtonStyled disabled={Boolean(!breakerBlockItem)} secondary={true} onClick={openBreakerModal}>
                    <MultiLingualText>Breakers</MultiLingualText>
                  </ButtonStyled>
                </Box>
              </>
            )}
          </Box>
        </BodyStyled>

        <CommodityFooter
          saveHandler={onSaveBlocks}
          loading={saveBlocksLoading}
          disableSave={saveBlocksLoading || hasErrors || isEqual(initialBlocks, blocks)}
          disableCancel={saveBlocksLoading || isEqual(initialBlocks, blocks)}
          cancelHandler={() => {
            setBlocks([...initialBlocks]);
            setSelectedBlock(initialBlocks[0]);
          }}
        />
      </Box>
      <PointSequenceModal visible={visible} close={close} onGeneratePoints={onPointsGenerated}></PointSequenceModal>
      {panelType && breakerBlockItem && visibleBreakerModal && (
        <BreakerModal
          visible={visibleBreakerModal}
          close={() => {
            closeBreakerModal();
          }}
          onBreakersUpdated={(breakerPanels: BreakerPanel[]) => {
            onBreakersUpdated(breakerPanels);
            closeBreakerModal();
          }}
          panelType={panelType}
          block={breakerBlockItem}
          canEdit={canEdit}
          commodityId={commodity.id}
        ></BreakerModal>
      )}
      {uiMessages && uiMessages.length > 0 && (
        <MessageModal messageKeys={uiMessages} onSubmit={handleQuestions}></MessageModal>
      )}
      {copyBlocksVisible && (
        <CopyBlocksModal
          onClose={(data) => {
            if (data) {
              onCopyBlocks(data);
            }
            setUiMessages([]);
            copyBlocksClose();
          }}
          blockCommodityType={blockCommodityType}
        />
      )}
      {copyPointsVisible && (
        <CopyPointsModal
          onClose={(data) => {
            if (data) {
              onCopyPoints(data);
            }
            setUiMessages([]);
            copyPointsClose();
          }}
          blockCommodityType={blockCommodityType}
        />
      )}
    </>
  );
};
